import type { History } from "history";
import type { LiveLectureMeetingLinkDto } from "../../interfaces/live-lecture/live-lecture.dto";
import { APP_IDS } from "../../constants/app-ids.constant";
import { store } from "../../store";
import {
  buildCallJoinUrl,
  getCallAppUrl,
  type CallJoinSession,
} from "../../utils/callJoinTransfer";

import { requestMicrophonePermission } from "./micPermission";

function buildReturnUrl(returnPath: string): string {
  if (returnPath.startsWith("http://") || returnPath.startsWith("https://")) {
    return returnPath;
  }

  return `${window.location.origin}${returnPath.startsWith("/") ? returnPath : `/${returnPath}`}`;
}

function getParentCallTheme(): string {
  try {
    const layout = JSON.parse(localStorage.getItem("layout") || "{}") as {
      layoutModeType?: string;
    };
    return layout.layoutModeType === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function buildSharedCallJoinSession(
  link: LiveLectureMeetingLinkDto,
  options?: { title?: string; returnPath?: string },
): CallJoinSession {
  const { auth, account } = store.getState() as {
    auth: { token: string | null };
    account: { userProfile?: { id?: string; name?: { en?: string; ar?: string }; profilePhoto?: string } };
  };
  const locale = localStorage.getItem("i18nextLng") || "en";
  const lang = locale.startsWith("ar") ? "ar" : "en";
  const environment = process.env.REACT_APP_ENVIRONMENT as string;
  const profile = account.userProfile;

  return {
    ...link,
    title: options?.title,
    returnUrl: buildReturnUrl(options?.returnPath || window.location.pathname),
    locale,
    theme: getParentCallTheme(),
    apiBaseUrl: process.env.REACT_APP_API_URL || `${window.location.origin}/api`,
    apiToken: auth.token || undefined,
    platformKey: APP_IDS[environment]?.web_cp,
    hostProfile: profile
      ? {
          id: profile.id,
          name: profile.name?.[lang] || profile.name?.en || profile.name?.ar,
          profilePhoto: profile.profilePhoto,
        }
      : undefined,
  };
}

export async function openLiveCall(
  link: LiveLectureMeetingLinkDto,
  history: History,
  options?: { title?: string; returnPath?: string },
): Promise<boolean> {
  const callAppUrl = getCallAppUrl();
  if (!callAppUrl) {
    return false;
  }

  if (!link.startMuted) {
    try {
      await requestMicrophonePermission();
    } catch {
      return false;
    }
  }

  window.location.assign(
    buildCallJoinUrl(
      callAppUrl,
      buildSharedCallJoinSession(link, {
        ...options,
        returnPath: options?.returnPath ?? history.location.pathname,
      }),
    ),
  );
  return true;
}
